import { SupportedLocale } from '@/_shared/types/languages';

export type CreatePaymentsParams = {
  amount: number;
  paymentType: CreatePaymentType;
  locale: `${SupportedLocale['value']}`;
};

export enum CreatePaymentType {
  RECHARGE = 'RECHARGE',
  SUBSCRIPTION = 'SUBSCRIPTION',
  ADDITIONAL_CONSUMPTION = 'ADDITIONAL_CONSUMPTION',
}

export enum PaymentStatus {
  PAID = 'PAID', // The payment was successfully paid
  PARTIALLY_PAID = 'PARTIALLY_PAID', // The payment was successfully partially paid
  REFUNDED = 'REFUNDED', // The payment has been returned to the sender
  CANCELED = 'CANCELED', // The payment was canceled before completion
  UNKNOWN = 'UNKNOWN', // error status
  PENDING = 'PENDING', // The payment is awaiting user that user execute the payment
  FAILED = 'FAILED', // The payment failed to process
}

export type GetPaymentsParams = {
  start: number;
  length: number;
  fromDate?: string;
  toDate?: string;
  fromAmount?: number;
  toAmount?: number;
  status?: PaymentStatus;
};

export type GetPendingPaymentsParams = Omit<GetPaymentsParams, 'status'>;

export type Currency = 'EUR' | 'USD';

export type BankInfo = {
  iban: string;
  accountHolderName: string;
  bic: string;
  country?: string | null;
};

export enum PaymentMethod {
  BANK_TRANSFER = 'BANK_TRANSFER', // Payment made via bank transfer
  CREDIT_CARD = 'CREDIT_CARD', // Payment made via credit card
}

type PaymentBaseType = {
  paymentId: string;
  paymentMethod: `${PaymentMethod}`;
  transactionName: string;
  amount: number;
  currency: Currency;
  paymentDate: string;
  productName: string;
  organizationId: string;
  organizationName?: string | null;
  status: `${keyof Omit<typeof PaymentStatus, 'PENDING'>}`;
};

type PaymentWithBankTransfer = {
  paymentMethod: `${PaymentMethod.BANK_TRANSFER}`;
  bankInfo: BankInfo;
};

type PaymentWithOtherMethod = {
  paymentMethod: `${keyof Omit<typeof PaymentMethod, 'BANK_TRANSFER'>}`;
  bankInfo?: undefined;
};

export type Payment =
  | (PaymentBaseType & PaymentWithBankTransfer)
  | (PaymentBaseType & PaymentWithOtherMethod);

export type PaymentFromBE = Omit<Payment, 'currency'> & {
  currency?: Currency;
} & (PaymentWithBankTransfer | PaymentWithOtherMethod);

export type PendingPaymentBaseType = Omit<PaymentBaseType, 'paymentDate' | 'status'> & {
  status: `${PaymentStatus.PENDING}`;
  paymentDate?: null;
};

export type PendingPayments =
  | (PendingPaymentBaseType & PaymentWithBankTransfer)
  | (PendingPaymentBaseType & PaymentWithOtherMethod);

export type PendingPaymentFromBE = Omit<PendingPayments, 'currency'> & {
  currency?: Currency;
} & (PaymentWithBankTransfer | PaymentWithOtherMethod);
